import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { trackConversion } from '../utils/analytics';
import { 
  CheckCircle2, 
  Loader2,
  XCircle,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  // Derive initial status from sessionId
  const initialStatus = sessionId ? 'loading' : 'error';
  const initialMessage = sessionId ? '' : 'No session ID found';
  
  const [status, setStatus] = useState(initialStatus);
  const [message, setMessage] = useState(initialMessage);
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (!sessionId || verifiedRef.current) return;

    let retryCount = 0;
    const maxRetries = 5;

    const verifyPayment = async () => {
      try {
        const response = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          verifiedRef.current = true;
          setStatus('success');
          setMessage('Your subscription has been activated!');
          toast.success('Welcome to Arlo Pro!');
          
          // Track purchase conversion
          const plan = response.data.plan_id || 'pro_monthly';
          const amount = response.data.amount || (plan === 'pro_yearly' ? 190 : 19);
          trackConversion.purchase(plan, amount, sessionId);
        } else if (response.data.status === 'expired') {
          verifiedRef.current = true;
          setStatus('error');
          setMessage('This checkout session has expired. Please try again.');
        } else {
          // Payment might still be processing, retry
          retryCount += 1;
          if (retryCount < maxRetries) {
            setTimeout(verifyPayment, 2000);
          } else {
            verifiedRef.current = true;
            setStatus('error');
            setMessage('Payment verification timed out. Please check your email for confirmation.');
          }
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        verifiedRef.current = true;
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Failed to verify payment');
      }
    };

    verifyPayment();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center"
      >
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Processing Payment</h1>
            <p className="text-muted-foreground mb-6">{message || 'Please wait while we verify your payment...'}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 15 }}
              className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center"
            >
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </motion.div>
            <h1 className="text-2xl font-bold mb-2">Welcome to Pro!</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/chat')}
                className="w-full gap-2"
              >
                Start Using Arlo Pro
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/pricing')}
                className="w-full"
              >
                View Subscription Details
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-destructive/20 flex items-center justify-center">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full"
              >
                Try Again
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/chat')}
                className="w-full"
              >
                Back to Arlo
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default BillingSuccessPage;
